/**
 * The new-experiment steps as one bar across the top, pinned while the page
 * scrolls: each step shows what was picked on it, so the experiment reads like
 * a recipe that fills in as you go, and the form below keeps the full width.
 */

import { Check } from "lucide-react";

export interface BarStep {
  name: string;
  /** What this step picked, once it has; empty while it is still open. */
  value?: string;
}

export function StepBar({
  label,
  steps,
  step,
  reachable,
  onPick,
}: {
  label: string;
  steps: readonly BarStep[];
  step: number;
  /** Steps after this one are not open yet. */
  reachable: number;
  onPick: (s: number) => void;
}) {
  return (
    <nav aria-label={label} className="sticky top-0 z-20 -mx-8 border-b border-line-soft bg-canvas px-8 py-3">
      <ol className="flex gap-1.5 overflow-x-auto">
        {steps.map(({ name, value }, i) => {
          const current = i === step;
          const done = !current && i < reachable && (i < step || !!value);
          return (
            <li key={name} className="flex min-w-[120px] flex-1 flex-col gap-2">
              <span
                aria-hidden
                className={`h-[3px] rounded-full transition-colors ${
                  current ? "bg-primary" : done ? "bg-link/60" : "bg-line"
                }`}
              />
              <button
                type="button"
                disabled={i > reachable}
                aria-current={current ? "step" : undefined}
                onClick={() => onPick(i)}
                className="group flex min-w-0 items-start gap-2 rounded-md text-left disabled:cursor-not-allowed"
              >
                <span
                  aria-hidden
                  className={`mt-px flex size-5 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold ${
                    current
                      ? "bg-primary text-on-primary"
                      : done
                        ? "bg-run-bg text-link"
                        : "border border-drop-border text-dim"
                  }`}
                >
                  {done ? <Check className="size-3" strokeWidth={2.75} /> : i + 1}
                </span>
                <span className="flex min-w-0 flex-col">
                  <span
                    className={`truncate text-[13px] font-medium ${
                      current ? "text-ink" : i > reachable ? "text-dim" : "text-body group-hover:text-ink"
                    }`}
                  >
                    {name}
                  </span>
                  <span className="truncate text-[11px] text-muted" title={value || undefined}>
                    {value || " "}
                  </span>
                </span>
              </button>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
